import React, { useState } from "react";
import { Grid, Divider, AppBar, Toolbar, IconButton, Avatar, MenuItem, Menu, Button, Typography, Badge, useScrollTrigger, Box, styled, useTheme, useMediaQuery } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import MoreVert from "@mui/icons-material/MoreVert";
import ExpandIcon from "@mui/icons-material/ExpandMore";
import PropTypes from "prop-types";
import * as ROUTES from "../../constants/routes";
import { logout } from "../../actions/auth_actions";
import { ReactComponent as SmLogo } from "../Common/Logo/smLogo.svg";
import { ReactComponent as SmallCoin } from "../Common/Icons/smallCoin.svg";

import { ReactComponent as JobsIcon } from "../Common/Icons/nav_jobs.svg";
import { ReactComponent as JobsSelectedIcon } from "../Common/Icons/nav_jobs_selected.svg";
import { ReactComponent as ProfileIcon } from "../Common/Icons/nav_profile.svg";
import { ReactComponent as ProfileSelectedIcon } from "../Common/Icons/nav_profile_selected.svg";
import { ReactComponent as MissionsIcon } from "../Common/Icons/nav_missions.svg";
import { ReactComponent as MissionsSelectedIcon } from "../Common/Icons/nav_missions_selected.svg";
import DescriptionTwoToneIcon from '@mui/icons-material/DescriptionTwoTone';
import NotificationsIcon from '@mui/icons-material/Notifications';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import NotificationsCenter from "./NotificationsCenter";
import {
  setNotificationAmount,
  setNotification
} from "../../actions/user_actions";
import {
  autoCompleteOccupation,
  triggerCustomerPortal
} from "../../actions/applicationAssistant_action";

import {
  manualOccSearch,
  addOccupationFilter,
} from "../../actions/job_actions";
import { EntitiesSearch } from "./EntitiesSearch";

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  maxHeight: 64,
  minHeight: 64,
  backgroundColor: "white",
}));

function ElevationScroll(props) {
  const { children } = props;
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 0,
  });

  return React.cloneElement(children, {
    elevation: trigger ? 2 : 10,
  });
}

const Navigation = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();

  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [hideNotification, setHideNotification] = useState(false);
  const [searchBar, setSearchBar] = useState(false);

  const {
    fullName = "",
    firstName = "",
    showChat = true,
    profileImageURL = "",
    customNavLogo = false,
    contractDetails = {},
    notificationAmount = 0,
    orgDetails = {},
    desiredExp = [],
    preferredOccupations = [],
    previousOccupations = [],
    internalMobilityProvider,
    internalMobilityRoles
  } = useSelector(state => state.user);
  const { fetchingJobs = false, occupationListFilter = [] } = useSelector(state => state.jobs);
  const { coins = 0 } = useSelector(state => state.rewards);
  

  const user = useSelector(state => state.user);
  let viewPermissions = {};
  if (user.providerDetails && user.providerDetails.viewPermissions) {
    viewPermissions = user.providerDetails.viewPermissions;
  }

  const occList = [...preferredOccupations, ...previousOccupations];

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setOpen(false);
  };

  const getVariant = (route) => {
    return location.pathname.indexOf(route) === 0 ? "primary" : "default";
  };

  const careerpathwayTitle = () => {
    if (user && user.providerDetails.viewPermissions) {
      if ((!user.providerDetails.viewPermissions.jobsCheckbox || !viewPermissions.promotedJobsCheckbox) && !user.providerDetails.viewPermissions.trainingCheckbox) {
        return "Career Pathway";
      } else if ((user.providerDetails.viewPermissions.jobsCheckbox || viewPermissions.promotedJobsCheckbox) && !user.providerDetails.viewPermissions.trainingCheckbox) {
        return "Training";
      } else {
        return "Jobs";
      }
    } else {
      return "Career Pathway";
    }
  };

  const { hideMissions = false, jobsType = 0 } = contractDetails;
  let orgName = (orgDetails.name) ? orgDetails.name : false;
  let orgInfo = (orgDetails.infoText) ? orgDetails.infoText : false;

  const hideMissonCheck = !viewPermissions.missionCheckbox || !viewPermissions.interventionCheckbox;
  const hideAACheck = !viewPermissions.cvBuilderCheckbox;

  const smallSettings = () => (
    <Box>
      <Badge badgeContent={notificationAmount} color="error" sx={{ display: 'flex' }}>
        <IconButton
          sx={{ color: hideNotification ? "#E16A23" : "#b4b8bf" }}
          size="small"
          onClick={() => setHideNotification(!hideNotification)}
        >
          <NotificationsIcon />
        </IconButton>
      </Badge>
    </Box>
  );


  return (
    <React.Fragment>
      <NotificationsCenter display={hideNotification} setDisplay={(h) => setHideNotification(h)} />
      <ElevationScroll>
        <StyledAppBar color="default">
          <Toolbar sx={{
            px: { xs: 0.625, sm: 0.625, md: 2, lg: 2 },
            '@media (min-width: 1250px)': {
              width: 1250,
              mx: "auto",
            }
          }}>
            <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
              <Grid container direction="row" justifyContent="flex-start" alignItems="center" sx={{ flexGrow: 1 }}>
                <Grid item>
                  {!customNavLogo && (
                    <Box sx={{
                      maxWidth: { xs: 58, sm: 86, md: 130 },
                      maxHeight: { xs: 28, sm: 30 },
                      flexGrow: { md: 1 },
                      display: 'flex',
                      '& svg': {
                        width: '100%',
                        height: 'auto',
                        maxHeight: '100%',
                      }
                    }}>
                      <SmLogo />
                    </Box>
                  )}
                  {customNavLogo && (
                    <Box component="img" src={customNavLogo} alt="Skillsminer" sx={{
                      maxWidth: { xs: 58, sm: 86, md: 130 }, maxHeight: { xs: 28, sm: 30 }, mr: { xs: 0.25, sm: 1.25, md: 2.5 }
                    }} />
                  )}
                </Grid>
                <Grid size="grow" sx={{ m: -0.625 }}>
                  <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', justifyContent: 'space-evenly' }}>
                      <Grid container direction="row" justifyContent="flex-start" alignItems="center">
                        <Grid item>
                          <Button component={Link} to={ROUTES.JOBS} disabled={showChat} color={getVariant(ROUTES.JOBS)} sx={{ minWidth: { xs: 'auto', md: 64 }, p: { xs: 1, md: 2 } }} startIcon={getVariant(ROUTES.JOBS) === "primary" ? <JobsSelectedIcon /> : <JobsIcon />} aria-label={careerpathwayTitle()}>
                            <Typography variant="body2" sx={{ display: { xs: 'none', md: searchBar ? 'none' : 'block' } }}>{careerpathwayTitle()}</Typography>
                          </Button>
                        </Grid>
                        {hideMissonCheck && (
                          <Grid item>
                            <Button component={Link} to={ROUTES.MISSIONS} disabled={showChat} color={getVariant(ROUTES.MISSIONS)} sx={{ minWidth: { xs: 'auto', md: 64 }, p: { xs: 1, md: 2 } }} startIcon={getVariant(ROUTES.MISSIONS) === "primary" ? <MissionsSelectedIcon /> : <MissionsIcon />} aria-label="Missions">
                              <Typography variant="body2" sx={{ display: { xs: 'none', md: searchBar ? 'none' : 'block' } }}>Missions</Typography>
                            </Button>
                          </Grid>
                        )}
                        <Grid item>
                          <Button component={Link} to={ROUTES.PROFILE} disabled={showChat} color={getVariant(ROUTES.PROFILE)} sx={{ minWidth: { xs: 'auto', md: 64 }, p: { xs: 1, md: 2 } }} startIcon={getVariant(ROUTES.PROFILE) === "primary" ? <ProfileSelectedIcon /> : <ProfileIcon />} aria-label="Profile">
                            <Typography variant="body2" sx={{ display: { xs: 'none', md: searchBar ? 'none' : 'block' } }}>Profile</Typography>
                          </Button>
                        </Grid>
                        {hideAACheck && (
                          <Grid item>
                            <Button component={Link} to={ROUTES.APPLICATION_ASSISTANT} disabled={showChat} color={getVariant(ROUTES.APPLICATION_ASSISTANT)} sx={{ minWidth: { xs: 'auto', md: 64 }, p: { xs: 1, md: 2 } }} startIcon={getVariant(ROUTES.APPLICATION_ASSISTANT) === "primary" ? <DescriptionTwoToneIcon sx={{ fontSize: 40, color: '#ff7e34', width: 25 }} /> : <DescriptionTwoToneIcon sx={{ fontSize: 40, color: '#B4B8BF', width: 25 }} />} aria-label="Application Assistant">
                              <Typography variant="body2" sx={{ display: { xs: 'none', md: searchBar ? 'none' : 'block' } }}>Application Assistant</Typography>
                            </Button>
                          </Grid>
                        )}
                      </Grid>
                      <Grid item sx={{ display: "flex", alignItems: "center" }}>
                        <Box>
                          {searchBar && (
                            <EntitiesSearch
                              placeholder={"What Occupation"}
                              disabled={showChat || fetchingJobs || !location.pathname.includes("jobs")}
                              autoCompleteOccupation={(value) => dispatch(autoCompleteOccupation(value))}
                              onSelect={(value) => dispatch(manualOccSearch(value))}
                              addOccupationFilter={(value) => dispatch(addOccupationFilter(value))}
                              occList={occList}
                              interalRolesOnly={internalMobilityProvider}
                              internalMobilityRoles={internalMobilityRoles}
                            />
                          )}
                        </Box>
                        <Box>
                          <IconButton onClick={() => setSearchBar(!searchBar)}>
                            {searchBar ? <CloseIcon /> : <SearchIcon />}
                          </IconButton>
                        </Box>
                      </Grid>
                  </Box>
                  <Box sx={{ display: { xs: 'flex', sm: 'none' }, alignItems: 'center', justifyContent: 'space-evenly' }}>
                      {!searchBar && (
                        <Grid container direction="row" justifyContent="space-evenly" alignItems="center">
                          <Grid item>
                            <IconButton aria-label="jobs" component={Link} disabled={showChat} to={ROUTES.JOBS}>
                              {getVariant(ROUTES.JOBS) === "primary" ? <JobsSelectedIcon /> : <JobsIcon />}
                            </IconButton>
                          </Grid>
                          {hideMissonCheck && (
                            <Grid item>
                              <IconButton aria-label="missions" component={Link} disabled={showChat} color={getVariant(ROUTES.MISSIONS)} to={ROUTES.MISSIONS}>
                                {getVariant(ROUTES.MISSIONS) === "primary" ? <MissionsSelectedIcon /> : <MissionsIcon />}
                              </IconButton>
                            </Grid>
                          )}
                          <Grid item>
                            <IconButton aria-label="profile" component={Link} disabled={showChat} to={ROUTES.PROFILE}>
                              {getVariant(ROUTES.PROFILE) === "primary" ? <ProfileSelectedIcon /> : <ProfileIcon />}
                            </IconButton>
                          </Grid>
                          {hideAACheck && (
                            <Grid item>
                              <IconButton aria-label="applicationAssistant" component={Link} disabled={showChat} to={ROUTES.APPLICATION_ASSISTANT}>
                                {getVariant(ROUTES.APPLICATION_ASSISTANT) === "primary" ? <DescriptionTwoToneIcon sx={{ fontSize: 40, color: '#ff7e34', width: 25 }} /> : <DescriptionTwoToneIcon sx={{ fontSize: 40, color: '#B4B8BF', width: 25 }} />}
                              </IconButton>
                            </Grid>
                          )}
                        </Grid>
                      )}
                    <Grid item sx={{ display: "flex", alignItems: "center" }}>
                      {searchBar && (
                        <EntitiesSearch
                          placeholder={"What Occupation"}
                          disabled={showChat || fetchingJobs || !location.pathname.includes("jobs")}
                          autoCompleteOccupation={(value) => dispatch(autoCompleteOccupation(value))}
                          onSelect={(value) => dispatch(manualOccSearch(value))}
                          addOccupationFilter={(value) => dispatch(addOccupationFilter(value))}
                          occList={occList}
                          interalRolesOnly={internalMobilityProvider}
                          internalMobilityRoles={internalMobilityRoles}
                        />
                      )}
                      <IconButton onClick={() => setSearchBar(!searchBar)}>
                        {searchBar ? <CloseIcon /> : <SearchIcon />}
                      </IconButton>
                    </Grid>
                    {!searchBar && smallSettings()}
                  </Box>
                </Grid>
              </Grid>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', width: "auto", [theme.breakpoints.down("sm")]: { width: 30 } }}>
              <Box sx={{ display: { lg: 'none' } }}>
                <IconButton style={{ padding: 0 }} aria-label="account of current user" onClick={handleMenu} color="inherit">
                  <MoreVert />
                </IconButton>
              </Box>
              {fullName !== "" && (
                <>
                  <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
                    <Badge badgeContent={notificationAmount} color="error" anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
                      <IconButton
                        sx={{ color: hideNotification ? "#E16A23" : "#b4b8bf" }}
                        size="small"
                        onClick={() => setHideNotification(!hideNotification)}
                      >
                        <NotificationsIcon />
                      </IconButton>
                    </Badge>
                    <IconButton aria-label="account of current user" onClick={handleMenu} color="inherit" size="small" sx={{ ml: 0.625 }}>
                      <Avatar alt={fullName} sx={{ width: 24, height: 24, overflow: "hidden" }} src={profileImageURL}>
                        {firstName.charAt(0)}
                      </Avatar>
                    </IconButton>
                  </Box>
                  <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
                    <Typography variant="body1" component="h6" onClick={handleMenu} sx={{ display: "inline", fontSize: 14, color: "#5A6375", fontWeight: "normal", cursor: "pointer" }}>
                      {fullName}
                    </Typography>
                    <ExpandIcon sx={{ width: 20, height: 30, color: "#B4B8BF", ml: 0.375, mb: -1.25 }} onClick={handleMenu} />
                  </Box>
                </>
              )}
              <Menu
                anchorEl={anchorEl}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
                keepMounted
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                open={open}
                onClose={handleClose}
              >
                <MenuItem disabled>{fullName}</MenuItem>
                <MenuItem disabled>
                  {`Coins: ${coins}`}
                  <SmallCoin />
                </MenuItem>
                <Divider />
                <MenuItem
                  onClick={async () => {
                    const portal = await dispatch(triggerCustomerPortal());
                    if (portal && portal.url) {
                      window.location.replace(portal.url);
                    }
                  }}
                  sx={{ minWidth: 150 }}
                >
                  Manage Billing
                </MenuItem>
                <Divider />
                <MenuItem
                  component={Link}
                  to={ROUTES.PROFILE_SETTINGS}
                  onClick={handleClose}
                  sx={{ minWidth: 150 }}
                  disabled={showChat}
                >
                  Settings
                </MenuItem>
                <Divider />
                {(orgName && orgInfo) && (
                  <MenuItem
                    component={Link}
                    to={ROUTES.ORGANISATION_PROFILE}
                    onClick={handleClose}
                    sx={{ minWidth: 150 }}
                    disabled={showChat}
                  >
                    {orgName}
                  </MenuItem>
                )}
                {(orgName && orgInfo) && <Divider />}
                <MenuItem sx={{ minWidth: 150 }} disabled={showChat}>
                  <a id="privacy-policy-link" href="https://www.skillsminer.ai/privacy-policy/" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'inherit' }}>
                    Privacy Policy
                  </a>
                </MenuItem>
                <Divider />
                <MenuItem
                  component={Link}
                  to={ROUTES.FAQ}
                  onClick={handleClose}
                  sx={{ minWidth: 150 }}
                  disabled={showChat}
                >
                  FAQs
                </MenuItem>
                <Divider />
                <MenuItem
                  onClick={() => {
                    handleClose(); // Close the menu first
                    dispatch(logout(navigate)); // Dispatch the logout action
                  }}
                  sx={{ minWidth: 150 }}
                >
                  Logout
                </MenuItem>
              </Menu>
            </Box>
          </Toolbar>
        </StyledAppBar>
      </ElevationScroll>
    </React.Fragment>
  );
};

export default Navigation;